import { css, cx } from "@emotion/css";
import { space, text, themeTokens, lineHeight } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { PopoverBasicHelpProps } from "../../Popover";
export interface InputLabelProps {
    /**
     * The ID of the input label
     */
    id?: string;
    /**
     * The label text to display
     */
    label: string;
    /**
     * The ID of the input element this label is for
     */
    htmlFor: string;
    /**
     * Whether the label should appear disabled
     */
    isDisabled?: boolean;
    /**
     * Whether the label should have a default marker
     */
    hasDefaultMarker?: boolean;
    /**
     * Whether the label should have a required marker
     */
    hasRequiredMarker?: boolean;
    /**
     * Whether the label should have an optional marker
     */
    hasOptionalMarker?: boolean;
    /**
     * PopoverBasicHelp component to display additional help information
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
    /**
     * Applys special styling for radio and checkbox implementations.
     */
    isRadioOrCheckbox?: boolean;
}
export function InputLabel({ id, label, htmlFor, isDisabled, popover, hasDefaultMarker, hasOptionalMarker, hasRequiredMarker, isRadioOrCheckbox }: InputLabelProps) {
    const defaultMarker = hasDefaultMarker && <span className={cx(suffixDefaultTextStyles, { [suffixDefaultTextDisabledStyles]: isDisabled })}> (Default)</span>;
    const optionalMarker = hasOptionalMarker && !hasRequiredMarker && " (optional)";
    const requiredMarker = hasRequiredMarker && !hasOptionalMarker && " (required)";
    return (<div className={inputLabelContainerStyles}>
            <label id={id} className={cx(inputLabelStyles, { [inputLabelRadioOrCheckboxStyles]: isRadioOrCheckbox, [inputLabelDisabledStyles]: isDisabled })} htmlFor={htmlFor}>
                {label} {optionalMarker} {requiredMarker} {defaultMarker}
            </label>
            {popover && <div className={popoverContainerStyles}>{popover}</div>}
        </div>);
}
export default InputLabel;
const inputLabelContainerStyles = css({
    display: "grid",
    gridTemplateColumns: `auto auto`,
    gap: space[4],
    justifyContent: "flex-start",
});
const inputLabelStyles = css({
    font: text.body.bold.large,
    lineHeight: lineHeight.xSmall,
    color: themeTokens.color.text.primary,
    margin: 0,
    cursor: "pointer",
});
const inputLabelRadioOrCheckboxStyles = css({
    font: text.body.regular.large,
    lineHeight: lineHeight.xSmall,
});
const inputLabelDisabledStyles = css({
    color: themeTokens.color.text.tertiary,
    cursor: "not-allowed",
});
const suffixDefaultTextStyles = css({
    color: themeTokens.color.text.tertiary,
});
const suffixDefaultTextDisabledStyles = css({
    color: themeTokens.color.text.tertiary,
    cursor: "not-allowed",
});
const popoverContainerStyles = css({
    transform: "translateY(-1px)",
});
